import classNames from 'classnames';
import {observer} from 'mobx-react';
import React from 'react';

import {Assertion, BadgeDetailsContextMenu} from '@udemy/badging';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import HereTickIcon from '@udemy/icons/dist/here-tick.ud-icon';
import {MarkdownContent} from '@udemy/react-code-components';
import {Image} from '@udemy/react-core-components';
import {
    ConsumerSubscriptionPlan,
    SubscriptionPlanContext,
    useConsumerSubscriptionPlan,
} from '@udemy/subscription-graphql';
import {useUDData} from '@udemy/ud-data';

import {AssertionStoreProps} from '../types/assertion-types';
import styles from './assertions-page.module.less';
import {AssertionShareMenu} from './share-menu.react-component';

export function isConsumerPlanSubscriber(
    consumerSubscriptionContext: SubscriptionPlanContext<ConsumerSubscriptionPlan>,
) {
    return (
        consumerSubscriptionContext &&
        'isSubscriber' in consumerSubscriptionContext &&
        consumerSubscriptionContext.isSubscriber
    );
}

export const InternalAssertionDetailBanner = ({assertionStore}: AssertionStoreProps) => {
    const {gettext} = useI18n();
    const isMobileMax = useMatchMedia('mobile-max');

    const udData = useUDData();
    const assertion: Assertion = assertionStore.assertion;

    const consumerSubscriptionContext = useConsumerSubscriptionPlan();
    const isPersonalPlanSubscriber = isConsumerPlanSubscriber(consumerSubscriptionContext);

    if (!assertion) {
        return null;
    }

    const isAssertionOwnerView =
        udData.me.is_authenticated && udData.me.id === parseInt(assertion.user.id, 10);

    const shouldShowShareMenu =
        udData.Config?.brand?.has_organization &&
        !udData.Config?.features?.organization?.hide_org_wide_learner_data;

    const hideViewCertPrepMenuItem =
        !udData.Config?.brand?.has_organization && !isPersonalPlanSubscriber;

    const contextMenu = (
        <div data-testid="context-menu-container" className={styles['context-menu-container']}>
            <BadgeDetailsContextMenu
                assertionStore={assertionStore}
                hideViewCertPrepMenuItem={hideViewCertPrepMenuItem}
            />
            {shouldShowShareMenu && <AssertionShareMenu assertion={assertionStore.assertion} />}
        </div>
    );

    return (
        <div
            className={classNames(styles['assertions-detail-banner'])}
            data-purpose={'assertion-detail-banner'}
        >
            <div className={styles['badge-class-image']}>
                <Image alt={assertion.badgeClass.name} src={assertion.badgeClass.image.id} />
                {/* Temporary placement until we have the share buttons */}
                {isMobileMax && isAssertionOwnerView && contextMenu}
            </div>
            <div className={styles['assertion-detail']}>
                <div className={styles['assertion-detail-title-bar']}>
                    <div className={styles['assertion-detail-title-container']}>
                        <div
                            className={classNames(
                                styles['certification-title'],
                                'ud-heading-serif-xxl',
                            )}
                            data-purpose={'certification-title'}
                        >
                            {assertion.badgeClass.name}
                        </div>
                        <div
                            className={styles['certification-issuer']}
                            data-purpose={'certification-issuer'}
                        >
                            {assertion.badgeClass.issuer.name}
                        </div>
                    </div>
                    {!isMobileMax && isAssertionOwnerView && contextMenu}
                </div>
                <div className={styles['assertion-fields']}>
                    <div className={styles['assertion-field']}>
                        <HereTickIcon size={'medium'} color={'positive'} />
                        <span className={'ud-text-md'}>{gettext('Verified')}</span>
                    </div>
                    {/* TODO: Replace with actual user name once validation is implemented */}
                    <div className={styles['assertion-field']}>
                        <span className={'ud-text-bold'}>{gettext('Issued to:')}</span>
                        <span
                            data-purpose={'assertion-issued-to'}
                        >{`${assertion.user.profile.name} ${assertion.user.profile.surname}`}</span>
                    </div>
                    <div className={styles['assertion-field']}>
                        <span className={'ud-text-bold'}>{gettext('Issued on:')}</span>
                        {/* TODO: date format after model creation*/}
                        <span data-purpose={'assertion-issue-date'}>{assertion.issuedOn}</span>
                    </div>
                    {assertion.expires && (
                        <div className={styles['assertion-field']}>
                            <span className={'ud-text-bold'}>{gettext('Expires on:')}</span>
                            <span data-purpose={'assertion-expiry-date'}>{assertion.expires}</span>
                        </div>
                    )}
                    <div className={styles['assertion-field']}>
                        <span className={'ud-text-bold'}>{gettext('Badge details:')}</span>
                    </div>
                    <div className={styles['assertion-field']}>
                        <span className={'ud-text-md'}>{assertion.badgeClass.description}</span>
                    </div>
                    {assertion.badgeClass.criteria.narrative && (
                        <>
                            <div className={styles['assertion-field']}>
                                <span className={'ud-text-bold'}>
                                    {gettext('Earning criteria:')}
                                </span>
                            </div>
                            <div className={styles['markdown']}>
                                <MarkdownContent>
                                    {assertion.badgeClass.criteria.narrative}
                                </MarkdownContent>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export const AssertionDetailBanner = observer(InternalAssertionDetailBanner);
